
import React, { useState } from 'react';
import { Type } from '@google/genai';
import { AIInsight, StudentConnection, UserProfile, WeeklyLog, Homework, StudentHomeworkStatus, StuckLog, AttendanceRecord, ClassInsight } from '../types';
import { ai, generateContentWithRetry } from '../lib/gemini';
import { Avatar } from './common';

export const WeeklyInsightsView = ({
    students,
    allUsers,
    logs,
    homeworks,
    statuses,
    stuckLogs,
    attendance,
    onDraftMessage,
    onPrepareHomework,
    t,
    classId,
    teacherId,
    existingInsight,
    onSaveInsight
}: {
    students: StudentConnection[];
    allUsers: UserProfile[];
    logs: WeeklyLog[];
    homeworks: Homework[];
    statuses: StudentHomeworkStatus[];
    stuckLogs: StuckLog[];
    attendance: AttendanceRecord[];
    onDraftMessage: (student: StudentConnection, parent: UserProfile, message: string) => void;
    onPrepareHomework: (description: string) => void;
    t: (key: string) => string;
    classId: string;
    teacherId: string;
    existingInsight: ClassInsight | undefined;
    onSaveInsight: (insight: Omit<ClassInsight, 'id'>) => Promise<void>;
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const insightData = existingInsight?.content;

    const generateInsights = async () => {
        setIsLoading(true);
        setError('');

        // 1. Gather data from the last 7 days
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        const recentLogs = logs.filter(l => new Date(l.date) >= oneWeekAgo);
        const recentHomeworks = homeworks.filter(h => new Date(h.date) >= oneWeekAgo);
        const recentHomeworkIds = recentHomeworks.map(h => h.id);
        const recentStatuses = statuses.filter(s => recentHomeworkIds.includes(s.homeworkId));
        const recentStuckLogs = stuckLogs.filter(s => recentHomeworkIds.includes(s.homeworkId));
        const recentAttendance = attendance.filter(a => new Date(a.date) >= oneWeekAgo);

        // 2. Format data for the prompt
        const promptData = {
            students: students.map(s => ({ id: s.id, name: s.childName })),
            weeklyLogs: recentLogs,
            homeworks: recentHomeworks,
            homeworkStatuses: recentStatuses,
            stuckLogs: recentStuckLogs,
            attendance: recentAttendance,
        };

        const prompt = `Analyze the following weekly data for my class and provide actionable insights.
        Data: ${JSON.stringify(promptData, null, 2)}`;
        
        try {
            const response = await generateContentWithRetry({
                model: 'gemini-3-pro-preview',
                contents: prompt,
                config: {
                    responseMimeType: 'application/json',
                    systemInstruction: `You are an expert teaching assistant. Your goal is to identify trends, pinpoint students who need attention, and suggest concrete actions for the teacher. Respond ONLY with a valid JSON object matching the provided schema.`,
                    responseSchema: {
                        type: Type.OBJECT,
                        properties: {
                            overallSummary: { type: Type.STRING, description: "A brief, 2-3 bullet point summary of the week's key trends." },
                            studentsToWatch: {
                                type: Type.ARRAY,
                                description: "A list of students who need attention.",
                                items: {
                                    type: Type.OBJECT,
                                    properties: {
                                        studentId: { type: Type.STRING },
                                        studentName: { type: Type.STRING },
                                        reason: { type: Type.STRING, description: "A concise, data-driven reason why this student is flagged." },
                                        suggestedAction: { type: Type.STRING, description: "A short, actionable suggestion, e.g., 'Check in about study stress' or 'Review fractions concept'." },
                                    },
                                    required: ['studentId', 'studentName', 'reason', 'suggestedAction']
                                }
                            },
                            suggestedClassActions: {
                                type: Type.ARRAY,
                                description: "A list of 1-2 suggested actions for the entire class.",
                                items: {
                                    type: Type.OBJECT,
                                    properties: {
                                        action: { type: Type.STRING, description: "e.g., 'Post an announcement' or 'Review a topic'." },
                                        details: { type: Type.STRING, description: "e.g., 'Clarify the instructions for the history project.'." }
                                    },
                                    required: ['action']
                                }
                            },
                        },
                        required: ['overallSummary', 'studentsToWatch', 'suggestedClassActions']
                    }
                }
            });

            // Robust JSON Parsing
            let jsonStr = response.text || '{}';
            // Clean markdown code blocks if present
            if (jsonStr.startsWith('```json')) {
                jsonStr = jsonStr.replace(/^```json\n/, '').replace(/\n```$/, '');
            } else if (jsonStr.startsWith('```')) {
                jsonStr = jsonStr.replace(/^```\n/, '').replace(/\n```$/, '');
            }
            
            const parsedInsights = JSON.parse(jsonStr) as AIInsight;
            
            await onSaveInsight({
                classId,
                teacherId,
                content: parsedInsights,
                generatedAt: new Date()
            });

        } catch (err) {
            console.error(err);
            setError("Sorry, I couldn't generate insights at this time. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleActionClick = (studentId: string, action: string) => {
        const student = students.find(s => s.id === studentId);
        const parent = allUsers.find(u => u.id === student?.parentId);
        if (!student || !parent) return;

        const message = `Hi ${parent.name}, I was reviewing ${student.childName}'s progress this week and wanted to check in about: ${action.toLowerCase()}. Is now a good time to chat briefly?`;
        onDraftMessage(student, parent, message);
    };

    return (
        <div className="p-4 space-y-4">
            {!insightData && (
                <div className="text-center p-8 bg-white rounded-xl shadow-sm border">
                    <span className="material-icons-round text-5xl text-indigo-400">auto_awesome</span>
                    <h3 className="text-xl font-bold mt-2">{t('insights')}</h3>
                    <p className="text-gray-500 text-sm mt-1 mb-4 max-w-sm mx-auto">Let AI analyze the last week's data to find trends, highlight students who need attention, and suggest an action plan.</p>
                    <button
                        onClick={generateInsights}
                        disabled={isLoading}
                        className="bg-indigo-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center mx-auto"
                    >
                        {isLoading ? (
                            <span>{t('analyzing_data')}</span>
                        ) : (
                            <span>{t('generate_weekly_insights')}</span>
                        )}
                    </button>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                </div>
            )}

            {insightData && (
                <div className="space-y-4">
                     <div className="flex justify-between items-center px-1">
                        <p className="text-xs text-gray-500">
                            Generated on {existingInsight?.generatedAt?.toLocaleString()}
                        </p>
                        <button onClick={generateInsights} disabled={isLoading} className="text-xs text-indigo-600 font-bold hover:underline">
                            {isLoading ? 'Generating...' : 'Refresh Insights'}
                        </button>
                     </div>

                     <div className="bg-white p-4 rounded-xl shadow-sm border">
                        <h3 className="font-bold text-lg">{t('overall_summary')}</h3>
                        <div className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{insightData.overallSummary}</div>
                     </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm border">
                        <h3 className="font-bold text-lg">{t('students_to_watch')}</h3>
                         <div className="mt-2 space-y-3">
                            {insightData.studentsToWatch.map(s => {
                                const student = students.find(stu => stu.id === s.studentId);
                                return (
                                <div key={s.studentId} className="p-3 bg-orange-50 border-l-4 border-orange-400 rounded-r-lg">
                                    <div className="flex items-center gap-2">
                                        <Avatar name={s.studentName} photoUrl={student?.photoUrl} size="sm" />
                                        <p className="font-bold">{s.studentName}</p>
                                    </div>
                                    <p className="text-sm text-gray-600 my-2">{s.reason}</p>
                                    <button 
                                      onClick={() => handleActionClick(s.studentId, s.suggestedAction)}
                                      className="text-sm font-bold bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full hover:bg-indigo-200"
                                    >
                                      {t('draft_message_to_parent')}
                                    </button>
                                </div>
                            )})}
                         </div>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm border">
                        <h3 className="font-bold text-lg">{t('suggested_actions')}</h3>
                         <div className="mt-2 space-y-2">
                            {insightData.suggestedClassActions.map((action, i) => (
                                <div key={i} className="p-3 bg-gray-50 rounded-lg">
                                    <p className="font-medium">{action.action}</p>
                                    <p className="text-sm text-gray-500">{action.details}</p>
                                </div>
                            ))}
                         </div>
                      </div>
                </div>
            )}
        </div>
    );
};
